package com.siriusxm.cmc.utils

import com.siriusxm.cmc.CMCContainer
import kotlinx.serialization.ExperimentalSerializationApi
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import kotlin.io.encoding.Base64
import kotlin.io.encoding.ExperimentalEncodingApi

/**
 * Helper to access images via the SXM CDN, using their resize API. Also handles proxies that CMC provides for
 * dev/prod images.
 */
public object CMCImages {
    @OptIn(ExperimentalSerializationApi::class)
    public val json: Json = Json {
        explicitNulls = false
    }

    public fun getImageUrl(
        baseUrl: String,
        path: String,
        resize: ResizeParams? = null,
        format: ImageFormat? = null
    ): String {
        val pathToUse = if (path.startsWith("/")) {
            path.substringAfter("/")
        } else {
            path
        }
        val imageSpec = ImageSpec(
            pathToUse,
            buildList {
                if (format != null) {
                    add(mapOf("format" to ImageParams(type = format.type)))
                }
                if (resize != null) {
                    add(mapOf("resize" to ImageParams(width = resize.width, height = resize.height)))
                }
            }
        )
        val jsonString = json.encodeToString(ImageSpec.serializer(), imageSpec)

        @OptIn(ExperimentalEncodingApi::class)
        val base64 = Base64.encode(jsonString.encodeToByteArray())
        return "${baseUrl}$base64"
    }
}

/**
 * Get an absolute image URL. Uses the image server from CMC, which provides some proxying for dev/prod images, since
 * many images that are referenced in dev are often only available in prod.
 */
public fun CMCContainer.getAbsoluteImageUrl(
    path: String, resize: ResizeParams = ResizeParams(600, 600),
    format: ImageFormat = ImageFormat.PNG
): String {
    val baseUrl = configProperty("atlasImageServerBaseUrl")!!
    return CMCImages.getImageUrl(baseUrl, path, resize, format)
}


@Serializable
public data class ImageSpec(
    val key: String,
    val edits: List<Map<String, ImageParams>>? = null
)

@Serializable
public data class ImageParams(
    val width: Int? = null,
    val height: Int? = null,
    val type: String? = null
)


/**
 * Resize parameters to pass for generating an image.
 */
public data class ResizeParams(
    val width: Int,
    val height: Int
) {
    public companion object {
        public fun constrainToWidth(width: Int, originalWidth: Int?, originalHeight: Int?): ResizeParams {
            val height =
                (((originalHeight ?: width).toFloat() / (originalWidth ?: width).toFloat()) * width.toFloat()).toInt()
            return ResizeParams(width, height)
        }
    }
}


public data class ImageFormat(
    val type: String
) {
    public companion object Companion {
        public val JPEG: ImageFormat = ImageFormat("jpeg")
        public val PNG: ImageFormat = ImageFormat("png")
    }
}